import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface TaxCalcResponse {
  calc_id: string;
  tax_type: string;
  period: string;
  amount_agorot: number;
  calculation: Record<string, unknown>;
  status: string;
  founder_approved_at: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function defaultPeriod(): string {
  return new Date().toISOString().slice(0, 7);
}

export function taxStatusColor(s: string): string {
  switch (s) {
    case 'pending': return '#f59e0b';
    case 'approved': return '#22c55e';
    case 'filed': return '#7dd3fc';
    default: return 'var(--omega-ink-subtle)';
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceTaxOverviewPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(defaultPeriod());

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data: vatData, isPending: vatPending, isError: vatError } = useQuery({
    queryKey: ['finance', 'tax', 'vat', period],
    queryFn: () => apiFetch<TaxCalcResponse>(`/finance/tax/vat?period=${period}`),
    refetchInterval: 30_000,
  });

  const { data: whData, isPending: whPending, isError: whError } = useQuery({
    queryKey: ['finance', 'tax', 'withholding', period],
    queryFn: () => apiFetch<TaxCalcResponse>(`/finance/tax/withholding?period=${period}`),
    refetchInterval: 30_000,
  });

  const sectionHeadStyle: React.CSSProperties = {
    fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)',
  };

  const navBtnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Taxes' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Tax Overview — Taxes
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={inputStyle}
          />
        </div>
      </div>

      {/* VAT section */}
      <section style={{ marginBottom: 28 }}>
        <div style={sectionHeadStyle}>VAT — VAT</div>
        {vatPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {vatError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}
        {vatData && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <StatCard label="VAT amount" value={fmtIls(vatData.amount_agorot)} highlight />
            <StatCard
              label="Status"
              value={vatData.status}
              color={taxStatusColor(vatData.status)}
            />
            <StatCard
              label="Approved by Founder"
              value={vatData.founder_approved_at ? fmtDate(vatData.founder_approved_at) : '—'}
            />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <Link to="/omega/finance/tax/vat" style={navBtnStyle}>VAT details →</Link>
        </div>
      </section>

      {/* Withholding section */}
      <section style={{ marginBottom: 28 }}>
        <div style={sectionHeadStyle}>Withholding tax — Withholding</div>
        {whPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {whError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}
        {whData && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <StatCard label="Withholding amount" value={fmtIls(whData.amount_agorot)} highlight />
            <StatCard
              label="Status"
              value={whData.status}
              color={taxStatusColor(whData.status)}
            />
            <StatCard
              label="Approved by Founder"
              value={whData.founder_approved_at ? fmtDate(whData.founder_approved_at) : '—'}
            />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <Link to="/omega/finance/tax/withholding" style={navBtnStyle}>Withholding-tax details →</Link>
        </div>
      </section>

      {/* Nav */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/omega/finance/tax/form-23" style={navBtnStyle}>Form 23</Link>
      </div>
    </div>
  );
}
